import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardMedia,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import toast from 'react-hot-toast';
import { getApiUrl } from '../config';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function Consultants() {
  const [consultants, setConsultants] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConsultant, setEditingConsultant] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    license_number: '',
    specializations: [],
    custom_sections: [],
    about: '',
    image: null,
    current_image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [newSpecialization, setNewSpecialization] = useState('');
  const [newCustomSection, setNewCustomSection] = useState({ title: '', items: [] });
  const [newCustomItem, setNewCustomItem] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    fetchConsultants();
  }, []);

  const fetchConsultants = async () => {
    try {
      const res = await fetch(getApiUrl('/api/consultants'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        
        // Parse JSON fields for each consultant
        const parsedData = data.map(consultant => {
          // Parse specializations
          let specializations = [];
          if (consultant.specializations) {
            try {
              if (typeof consultant.specializations === 'string') {
                specializations = JSON.parse(consultant.specializations);
              } else {
                specializations = consultant.specializations;
              }
            } catch (e) {
              console.error('Error parsing specializations:', e);
              specializations = [];
            }
          }
          
          // Parse custom_sections
          let customSections = [];
          if (consultant.custom_sections) {
            try {
              if (typeof consultant.custom_sections === 'string') {
                customSections = JSON.parse(consultant.custom_sections);
              } else {
                customSections = consultant.custom_sections;
              }
            } catch (e) {
              console.error('Error parsing custom_sections:', e);
              customSections = [];
            }
          }
          
          return {
            ...consultant,
            specializations,
            custom_sections: customSections
          };
        });
        
        console.log('Parsed consultants data:', parsedData);
        setConsultants(parsedData);
      }
    } catch (error) {
      console.error('Error fetching consultants:', error);
      toast.error('خطا در دریافت مشاوران');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formDataToSend = new FormData();
    formDataToSend.append('first_name', formData.first_name);
    formDataToSend.append('last_name', formData.last_name);
    formDataToSend.append('license_number', formData.license_number);
    formDataToSend.append('specializations', JSON.stringify(formData.specializations));
    formDataToSend.append('custom_sections', JSON.stringify(formData.custom_sections));
    formDataToSend.append('about', formData.about);
    
    // Debug: نمایش داده‌های ارسالی
    console.log('Form Data:', {
      first_name: formData.first_name,
      last_name: formData.last_name,
      license_number: formData.license_number,
      specializations: formData.specializations,
      custom_sections: formData.custom_sections,
      about: formData.about
    });
    
    // اگر تصویر جدید انتخاب شده باشد
    if (formData.image) {
      formDataToSend.append('image', formData.image);
    }
    // اگر در حال ویرایش هستیم و تصویر قبلی وجود دارد
    else if (editingConsultant && formData.current_image) {
      formDataToSend.append('current_image', formData.current_image);
    }

    try {
      const url = editingConsultant 
        ? getApiUrl(`/api/consultants/${editingConsultant.id}`)
        : getApiUrl('/api/consultants');
      
      const method = editingConsultant ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataToSend
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(editingConsultant ? 'مشاور با موفقیت به‌روزرسانی شد' : 'مشاور با موفقیت ایجاد شد');
        setOpenDialog(false);
        resetForm();
        fetchConsultants();
      } else {
        const error = await res.json();
        toast.error(error.error || 'خطا در ذخیره مشاور');
      }
    } catch (error) {
      console.error('Error saving consultant:', error);
      toast.error('خطا در ذخیره مشاور');
    }
  };

  const handleImageUpload = async (file) => {
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleEdit = (consultant) => {
    setEditingConsultant(consultant);
    
    // Debug: نمایش داده‌های دریافتی
    console.log('Consultant data received:', consultant);
    console.log('custom_sections:', consultant.custom_sections);
    console.log('specializations:', consultant.specializations);
    
    setFormData({
      first_name: consultant.first_name,
      last_name: consultant.last_name,
      license_number: consultant.license_number,
      specializations: consultant.specializations || [],
      custom_sections: consultant.custom_sections || [],
      about: consultant.about || '',
      image: null,
      current_image: consultant.image
    });
    setImagePreview(consultant.image ? getApiUrl(consultant.image) : null);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('آیا از حذف این مشاور اطمینان دارید؟')) {
      try {
        const res = await fetch(getApiUrl(`/api/consultants/${id}`), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (res.ok) {
          toast.success('مشاور با موفقیت حذف شد');
          fetchConsultants();
        } else {
          const error = await res.json();
          toast.error(error.error || 'خطا در حذف مشاور');
        }
      } catch (error) {
        console.error('Error deleting consultant:', error);
        toast.error('خطا در حذف مشاور');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      license_number: '',
      specializations: [],
      custom_sections: [],
      about: '',
      image: null,
      current_image: null
    });
    setImagePreview(null);
    setEditingConsultant(null);
    setNewSpecialization('');
    setNewCustomSection({ title: '', items: [] });
    setNewCustomItem('');
    setEditingSection(null);
    setEditingItem(null);
  };

  const handleAddNew = () => {
    resetForm();
    setOpenDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // بررسی حجم فایل (100KB = 100 * 1024 bytes)
      if (file.size > 100 * 1024) {
        toast.error('حجم تصویر نباید بیشتر از 100 کیلوبایت باشد');
        return;
      }
      handleImageUpload(file);
    }
  };

  const addSpecialization = () => {
    if (newSpecialization.trim()) {
      setFormData(prev => ({
        ...prev,
        specializations: [...prev.specializations, newSpecialization.trim()]
      }));
      setNewSpecialization('');
    }
  };

  const removeSpecialization = (index) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.filter((_, i) => i !== index)
    }));
  };

  // مدیریت بخش‌های اختصاصی
  const addCustomItem = () => {
    if (newCustomItem.trim()) {
      setNewCustomSection(prev => ({
        ...prev,
        items: [...prev.items, newCustomItem.trim()]
      }));
      setNewCustomItem('');
    }
  };

  const removeCustomItem = (index) => {
    setNewCustomSection(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const addCustomSection = () => {
    if (newCustomSection.title.trim() && newCustomSection.items.length > 0) {
      setFormData(prev => ({
        ...prev,
        custom_sections: [...prev.custom_sections, {
          title: newCustomSection.title.trim(),
          items: [...newCustomSection.items]
        }]
      }));
      setNewCustomSection({ title: '', items: [] });
    }
  };

  const removeCustomSection = (index) => {
    setFormData(prev => ({
      ...prev,
      custom_sections: prev.custom_sections.filter((_, i) => i !== index)
    }));
  };

  // ویرایش بخش موجود
  const editCustomSection = (sectionIndex) => {
    const section = formData.custom_sections[sectionIndex];
    setEditingSection(sectionIndex);
    setNewCustomSection({
      title: section.title,
      items: [...section.items]
    });
  };

  // ذخیره ویرایش بخش
  const saveCustomSectionEdit = () => {
    if (newCustomSection.title.trim() && newCustomSection.items.length > 0) {
      setFormData(prev => ({
        ...prev,
        custom_sections: prev.custom_sections.map((section, index) => 
          index === editingSection ? {
            title: newCustomSection.title.trim(),
            items: [...newCustomSection.items]
          } : section
        )
      }));
      setNewCustomSection({ title: '', items: [] });
      setEditingSection(null);
    }
  };

  // لغو ویرایش بخش
  const cancelCustomSectionEdit = () => {
    setNewCustomSection({ title: '', items: [] });
    setEditingSection(null);
  };

  // ویرایش آیتم موجود
  const editCustomItem = (sectionIndex, itemIndex) => {
    const item = formData.custom_sections[sectionIndex].items[itemIndex];
    setEditingItem({ sectionIndex, itemIndex, value: item });
  };

  // ذخیره ویرایش آیتم
  const saveCustomItemEdit = () => {
    if (editingItem && editingItem.value.trim()) {
      setFormData(prev => ({
        ...prev,
        custom_sections: prev.custom_sections.map((section, sectionIdx) => 
          sectionIdx === editingItem.sectionIndex ? {
            ...section,
            items: section.items.map((item, itemIdx) => 
              itemIdx === editingItem.itemIndex ? editingItem.value.trim() : item
            )
          } : section
        )
      }));
      setEditingItem(null);
    }
  };

  // لغو ویرایش آیتم
  const cancelCustomItemEdit = () => {
    setEditingItem(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          مدیریت مشاوران
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          افزودن مشاور جدید
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>تصویر</TableCell>
              <TableCell>نام و نام خانوادگی</TableCell>
              <TableCell>شماره نظام وظیفه</TableCell>
              <TableCell>تخصص‌ها</TableCell>
              <TableCell>تاریخ ایجاد</TableCell>
              <TableCell>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {consultants.map((consultant) => (
              <TableRow key={consultant.id}>
                <TableCell>
                  {consultant.image && (
                    <img
                      src={getApiUrl(consultant.image)}
                      alt={`${consultant.first_name} ${consultant.last_name}`}
                      style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {consultant.first_name} {consultant.last_name}
                </TableCell>
                <TableCell>{consultant.license_number}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {consultant.specializations?.slice(0, 3).map((spec, index) => (
                      <Chip key={index} label={spec} size="small" />
                    ))}
                    {consultant.specializations?.length > 3 && (
                      <Chip label={`+${consultant.specializations.length - 3}`} size="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{formatDate(consultant.created_at)}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(consultant)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(consultant.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <EditIcon sx={{ color: 'white', fontSize: '20px' }} />
            </Box>
            <Box>
              <Typography variant="h6" component="div">
                {editingConsultant ? 'ویرایش مشاور' : 'افزودن مشاور جدید'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editingConsultant ? 'اطلاعات مشاور را ویرایش کنید' : 'اطلاعات مشاور جدید را وارد کنید'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={3}>
            {/* اطلاعات شخصی */}
            <Grid item xs={12}>
              <Card sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <EditIcon sx={{ color: 'white', fontSize: '16px' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    اطلاعات شخصی
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="نام"
                      value={formData.first_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      required
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="نام خانوادگی"
                      value={formData.last_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      required
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="شماره نظام وظیفه"
                      value={formData.license_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, license_number: e.target.value }))}
                      required
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* تصویر پروفایل */}
            <Grid item xs={12}>
              <Card sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <EditIcon sx={{ color: 'white', fontSize: '16px' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    تصویر پروفایل
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      حداکثر حجم تصویر: 100 کیلوبایت
                    </Typography>
                    <input
                      accept="image/*"
                      type="file"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                      id="image-upload"
                    />
                    <label htmlFor="image-upload">
                      <Button variant="outlined" component="span" startIcon={<i className="fa fa-upload"></i>}>
                        انتخاب تصویر
                      </Button>
                    </label>
                  </Box>
                  
                  {imagePreview && (
                    <Box sx={{ textAlign: 'center' }}>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ 
                          width: 120, 
                          height: 120, 
                          objectFit: 'cover', 
                          borderRadius: 8,
                          border: '2px solid #e0e0e0'
                        }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => {
                          setImagePreview(null);
                          setFormData(prev => ({ ...prev, image: null }));
                        }}
                        sx={{ mt: 1 }}
                      >
                        حذف تصویر
                      </Button>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
              
            {/* تخصص‌ها */}
            <Grid item xs={12}>
              <Card sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="fa fa-graduation-cap" style={{ color: 'white', fontSize: '16px' }}></i>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    تخصص‌ها
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="افزودن تخصص جدید"
                    value={newSpecialization}
                    onChange={(e) => setNewSpecialization(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialization())}
                    variant="outlined"
                    size="small"
                  />
                  <Button onClick={addSpecialization} variant="contained" startIcon={<AddIcon />}>
                    افزودن
                  </Button>
                </Box>
                
                {formData.specializations.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      تخصص‌های موجود:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.specializations.map((spec, index) => (
                        <Chip
                          key={index}
                          label={spec}
                          onDelete={() => removeSpecialization(index)}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Card>
            </Grid>

            {/* بخش‌های اختصاصی */}
            <Grid item xs={12}>
              <Card sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="fa fa-puzzle-piece" style={{ color: 'white', fontSize: '16px' }}></i>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    بخش‌های اختصاصی
                  </Typography>
                </Box>
                
                {/* افزودن بخش جدید */}
                <Card sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5', border: '1px dashed #ccc' }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    افزودن بخش جدید
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      fullWidth
                      label="عنوان بخش (مثل: مدارک، افتخارات، تجربیات)"
                      value={newCustomSection.title}
                      onChange={(e) => setNewCustomSection(prev => ({ ...prev, title: e.target.value }))}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  
                  {/* افزودن آیتم به بخش */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      fullWidth
                      label="افزودن آیتم جدید"
                      value={newCustomItem}
                      onChange={(e) => setNewCustomItem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomItem())}
                      variant="outlined"
                      size="small"
                    />
                    <Button onClick={addCustomItem} variant="outlined" size="small" startIcon={<AddIcon />}>
                      افزودن آیتم
                    </Button>
                  </Box>
                  
                  {/* لیست آیتم‌های بخش */}
                  {newCustomSection.items.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        آیتم‌های بخش:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {newCustomSection.items.map((item, index) => (
                          <Chip
                            key={index}
                            label={item}
                            onDelete={() => removeCustomItem(index)}
                            color="secondary"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  
                  <Button 
                    onClick={addCustomSection} 
                    variant="contained" 
                    disabled={!newCustomSection.title.trim() || newCustomSection.items.length === 0}
                    fullWidth
                    startIcon={<AddCircleIcon />}
                  >
                    افزودن بخش
                  </Button>
                </Card>
                
                {/* نمایش بخش‌های موجود */}
                {formData.custom_sections && formData.custom_sections.length > 0 && (
                  <>
                    {formData.custom_sections.map((section, sectionIndex) => (
                      <Card key={sectionIndex} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                            {section.title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton 
                              onClick={() => editCustomSection(sectionIndex)}
                              color="primary"
                              size="small"
                              title="ویرایش بخش"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton 
                              onClick={() => removeCustomSection(sectionIndex)}
                              color="error"
                              size="small"
                              title="حذف بخش"
                            >
                              <RemoveCircleIcon />
                            </IconButton>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {section.items && section.items.map((item, itemIndex) => (
                            <Box key={itemIndex} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip
                                label={item}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                              <IconButton 
                                onClick={() => editCustomItem(sectionIndex, itemIndex)}
                                size="small"
                                sx={{ p: 0.5 }}
                                title="ویرایش آیتم"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      </Card>
                    ))}
                  </>
                )}
              </Card>
            </Grid>

            {/* درباره مشاور */}
            <Grid item xs={12}>
              <Card sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="fa fa-file-text" style={{ color: 'white', fontSize: '16px' }}></i>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    درباره مشاور
                  </Typography>
                </Box>
                
                <ReactQuill
                  value={formData.about}
                  onChange={(value) => setFormData(prev => ({ ...prev, about: value }))}
                  theme="snow"
                  style={{ height: '300px', marginBottom: '50px' }}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'color': [] }, { 'background': [] }],
                      [{ 'align': [] }],
                      ['link', 'image'],
                      ['clean']
                    ]
                  }}
                />
              </Card>
            </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button 
              onClick={() => setOpenDialog(false)}
              variant="outlined"
            >
              انصراف
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{ 
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                }
              }}
            >
              {editingConsultant ? 'به‌روزرسانی' : 'ایجاد'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog ویرایش بخش */}
      <Dialog open={editingSection !== null} onClose={cancelCustomSectionEdit} maxWidth="sm" fullWidth>
        <DialogTitle>ویرایش بخش</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="عنوان بخش"
            value={newCustomSection.title}
            onChange={(e) => setNewCustomSection(prev => ({ ...prev, title: e.target.value }))}
            margin="normal"
            variant="outlined"
          />
          
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            آیتم‌های بخش:
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="افزودن آیتم جدید"
              value={newCustomItem}
              onChange={(e) => setNewCustomItem(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomItem())}
              variant="outlined"
              size="small"
            />
            <Button onClick={addCustomItem} variant="outlined" size="small" startIcon={<AddIcon />}>
              افزودن
            </Button>
          </Box>
          
          {newCustomSection.items.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {newCustomSection.items.map((item, index) => (
                <Chip
                  key={index}
                  label={item}
                  onDelete={() => removeCustomItem(index)}
                  color="secondary"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelCustomSectionEdit}>انصراف</Button>
          <Button onClick={saveCustomSectionEdit} variant="contained">ذخیره</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog ویرایش آیتم */}
      <Dialog open={editingItem !== null} onClose={cancelCustomItemEdit} maxWidth="sm" fullWidth>
        <DialogTitle>ویرایش آیتم</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="متن آیتم"
            value={editingItem?.value || ''}
            onChange={(e) => setEditingItem(prev => ({ ...prev, value: e.target.value }))}
            margin="normal"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelCustomItemEdit}>انصراف</Button>
          <Button onClick={saveCustomItemEdit} variant="contained">ذخیره</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Consultants; 